import {
  useEffect,
  useState,
} from "react";
import {
  Link,
  useSearchParams,
} from "react-router-dom";

import {
  getEventCircleInvitation,
  respondToEventCircle,
} from "./eventCircle.repository";

import {
  editLabel,
  eventCircleResponses,
  formatEventDateTime,
  responseLabel,
  type EventCircle,
  type InviteeResponse,
} from "./eventCircle.types";

import "./EventCircleInvitationPage.css";

function errorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    error.message
  ) {
    return error.message;
  }

  return "This Event Circle invitation is unavailable.";
}

function visibilityText(circle: EventCircle): string {
  return circle.visibility === "PUBLIC"
    ? "Public Event Circle"
    : "Private Event Circle";
}

export function EventCircleInvitationPage() {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") || "";

  const [circle, setCircle] =
    useState<EventCircle | null>(null);

  const [loading, setLoading] = useState(true);
  const [responding, setResponding] =
    useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function loadInvitation() {
    if (!token) {
      setError(
        "This Event Circle link is missing its invitation code.",
      );

      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      setCircle(
        await getEventCircleInvitation(token),
      );
    } catch (caught) {
      setCircle(null);
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadInvitation();
  }, [token]);

  async function chooseResponse(
    response: InviteeResponse,
  ) {
    if (!circle || circle.status === "CANCELLED") {
      return;
    }

    setResponding(true);
    setNotice("");
    setError("");

    try {
      const updated = await respondToEventCircle(
        token,
        response,
      );

      setCircle(updated);

      setNotice(
        `Your response is ${responseLabel(
          updated.responseStatus,
        )}.`,
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setResponding(false);
    }
  }

  return (
    <main className="event-invitation-page">
      <section className="event-invitation-card">
        <header className="event-invitation-card__brand">
          <Link to="/">
            SARADIUM
          </Link>

          <span>EVENT CIRCLE</span>
        </header>

        {loading && (
          <div className="event-invitation-state">
            <div className="spinner" />

            <p>Opening Event Circle…</p>
          </div>
        )}

        {!loading && error && (
          <div className="event-invitation-state">
            <span className="event-invitation-state__symbol">
              !
            </span>

            <h1>Invitation unavailable</h1>

            <p>{error}</p>

            <button
              className="button secondary"
              type="button"
              onClick={() => void loadInvitation()}
            >
              Try again
            </button>

            <Link
              className="button primary"
              to="/app/event-circle"
            >
              Open Event Circle
            </Link>
          </div>
        )}

        {!loading && circle && (
          <>
            <section className="event-invitation-card__hero">
              <div className="event-invitation-card__circle">
                ◌
              </div>

              <span>{visibilityText(circle)}</span>

              <h1>{circle.title}</h1>

              <p>
                {circle.status === "CANCELLED"
                  ? "This event was cancelled by the host."
                  : formatEventDateTime(
                      circle.startsAt,
                    )}
              </p>
            </section>

            <section className="event-invitation-card__details">
              <article>
                <span>WHEN</span>

                <strong>
                  {formatEventDateTime(
                    circle.startsAt,
                  )}
                </strong>

                <p>
                  Until{" "}
                  {formatEventDateTime(
                    circle.endsAt,
                  )}
                </p>
              </article>

              <article>
                <span>WHERE</span>

                <strong>
                  {circle.location ||
                    "Location will be confirmed by the host"}
                </strong>

                <p>
                  {circle.visibility === "PUBLIC"
                    ? "This event can be shared."
                    : "This invitation is linked to your account."}
                </p>
              </article>

              <article>
                <span>EVENT HISTORY</span>

                <strong>
                  {editLabel(circle.editCount)}
                </strong>

                <p>
                  Host changes update saved event details
                  automatically.
                </p>
              </article>
            </section>

            {circle.description && (
              <section className="event-invitation-card__description">
                <span>ABOUT THIS EVENT</span>

                <p>{circle.description}</p>
              </section>
            )}

            {notice && (
              <section className="message success">
                {notice}
              </section>
            )}

            {circle.status === "CANCELLED" ? (
              <section className="event-invitation-card__cancelled">
                <strong>Event cancelled</strong>

                <p>
                  The host cancelled this Event Circle.
                  It will no longer be active in your
                  calendar.
                </p>
              </section>
            ) : (
              <section className="event-invitation-card__response">
                <div>
                  <span>WILL YOU BE THERE?</span>

                  <h2>Your response</h2>

                  <p>
                    The host can see your response, but
                    only the host can edit the event.
                  </p>
                </div>

                <div className="event-invitation-card__choices">
                  {eventCircleResponses.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      disabled={responding}
                      className={[
                        "event-invitation-choice",
                        circle.responseStatus ===
                        option.value
                          ? "is-selected"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() =>
                        void chooseResponse(option.value)
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <footer className="event-invitation-card__footer">
              <span>
                {circle.visibility === "PUBLIC"
                  ? "Anyone with this public link can view and respond."
                  : "Private invitations cannot be shared with another account."}
              </span>

              <Link to="/app/event-circle">
                Open in SARADIUM →
              </Link>
            </footer>
          </>
        )}
      </section>
    </main>
  );
}